import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";

import { apiClient, API_BASE_URL, extractApiError } from "../api/client";
import {
  useSystemConfig,
  useSystemStatus,
  useUpdateConfig,
  useValidateConfig,
  useRunAccount,
  useRebuildMetadataIndex,
  useMetadataRebuildStatus,
  useExportDatabaseBackup,
  useDatabaseBackupStatus,
  useDatabaseBackups,
  useValidateDatabaseBackupImport,
  useImportDatabaseBackup,
  useDatabaseBackupImportStatus,
  useDeleteAccount,
  useApiTokens,
  useCreateApiToken,
  useDeleteApiToken,
  buildDatabaseBackupDownloadUrl,
  useIamUsers,
  useIamGroups,
  useCreateIamUser,
  useUpdateIamUser,
  useDeleteIamUser,
  useResetIamUserPassword,
  useCreateIamGroup,
  useUpdateIamGroup,
  useDeleteIamGroup,
  useAddUserToGroup,
  useRemoveUserFromGroup,
  useS3TieringStatus,
  useRunS3TieringAction,
  useConfigureS3Tiering,
  useS3TieringPrunePrecheck,
  S3TieringAction
} from "../api/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ThemeMode, useThemeStore } from "../store/themeStore";
import { useSettingsStore } from "../store/settingsStore";
import { useNotificationStore } from "../store/notificationStore";
import { useAuthStore } from "../store/authStore";
import { BackupWizardDialog } from "../components/BackupWizardDialog";
import type {
  AccountConfig,
  AuthUserConfig,
  ConfigDocument,
  NotificationSmtpConfig,
  S3Config,
  IAMUserSummary,
  S3TieringPrunePrecheckResponse
} from "../api/hooks";

const refreshOptions = [
  { label: "10 secondi", value: 10_000 },
  { label: "15 secondi", value: 15_000 },
  { label: "30 secondi", value: 30_000 },
  { label: "1 minuto", value: 60_000 },
  { label: "5 minuti", value: 300_000 }
];

const notificationDurations = [3_000, 6_000, 10_000, 15_000];
const searchRowsOptions = [10, 25, 50, 100];
const sourceSyncIntervalOptions = [1, 3, 6, 12];

function formatMillis(value: number) {
  if (value % 60_000 === 0) {
    const minutes = value / 60_000;
    return minutes === 1 ? "1 minuto" : `${minutes} minuti`;
  }
  return `${Math.round(value / 1_000)} secondi`;
}

function cloneConfig(config: ConfigDocument): ConfigDocument {
  return JSON.parse(JSON.stringify(config)) as ConfigDocument;
}

function defaultAccount(): AccountConfig {
  return {
    username: "",
    label: "",
    password: "",
    host: "",
    port: 993,
    folders: ["INBOX"]
  };
}

function defaultS3Config(): S3Config {
  return {
    bucket: "",
    region: "",
    prefix: "",
    endpoint_url: "",
    aws_access_key_id: "",
    aws_secret_access_key: ""
  };
}

function defaultAuthUser(): AuthUserConfig {
  return {
    username: "",
    password: ""
  };
}

function parseListInput(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function formatIamLoadError(error: unknown): string {
  const raw = extractApiError(error);
  if (raw.includes("[keycloak_admin_credentials_missing]")) {
    return "Mancano le credenziali admin Keycloak nel container API (KEYCLOAK_ADMIN / KEYCLOAK_ADMIN_PASSWORD). Aggiorna compose e riavvia lo stack.";
  }
  if (raw.includes("[keycloak_auth_failed]")) {
    return "Autenticazione admin verso Keycloak fallita. Verifica KEYCLOAK_ADMIN, KEYCLOAK_ADMIN_PASSWORD e KEYCLOAK_ADMIN_REALM.";
  }
  if (raw.includes("[keycloak_unreachable]")) {
    return "Keycloak non raggiungibile dal backend API. Verifica KEYCLOAK_INTERNAL_BASE_URL e stato container keycloak.";
  }
  return raw;
}

function formatDateTime(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value < 0) {
    return "0 B";
  }
  if (value < 1024) {
    return `${value} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let size = value / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

export function Settings() {
  const queryClient = useQueryClient();
  const { mode, setMode } = useThemeStore();
  const statusRefreshMs = useSettingsStore((state) => state.statusRefreshMs);
  const setStatusRefreshMs = useSettingsStore((state) => state.setStatusRefreshMs);
  const activityRefreshMs = useSettingsStore((state) => state.activityRefreshMs);
  const setActivityRefreshMs = useSettingsStore((state) => state.setActivityRefreshMs);
  const notificationsAutoDismiss = useSettingsStore((state) => state.notificationsAutoDismiss);
  const setNotificationsAutoDismiss = useSettingsStore((state) => state.setNotificationsAutoDismiss);
  const notificationsDurationMs = useSettingsStore((state) => state.notificationsDurationMs);
  const setNotificationsDurationMs = useSettingsStore((state) => state.setNotificationsDurationMs);
  const searchDefaultRows = useSettingsStore((state) => state.searchDefaultRows);
  const setSearchDefaultRows = useSettingsStore((state) => state.setSearchDefaultRows);
  const resetDefaults = useSettingsStore((state) => state.resetDefaults);
  const enqueueNotification = useNotificationStore((state) => state.enqueue);
  const canManageSettings = useAuthStore((state) => state.canManageSettings());
  const canManageS3Tiering = useAuthStore((state) => state.canManageS3Tiering());
  const currentAuthProvider = useAuthStore((state) => state.provider);
  const { data: systemStatus, isError, error } = useSystemStatus(statusRefreshMs);
  const configQuery = useSystemConfig();
  const updateConfig = useUpdateConfig();
  const validateConfig = useValidateConfig();
  const runAccount = useRunAccount();
  const rebuildMetadataIndex = useRebuildMetadataIndex();
  const metadataRebuildStatus = useMetadataRebuildStatus();
  const exportDatabaseBackup = useExportDatabaseBackup();
  const databaseBackupStatusQuery = useDatabaseBackupStatus();
  const databaseBackupsQuery = useDatabaseBackups();
  const validateDatabaseBackupImport = useValidateDatabaseBackupImport();
  const importDatabaseBackup = useImportDatabaseBackup();
  const databaseBackupImportStatusQuery = useDatabaseBackupImportStatus();
  const deleteAccount = useDeleteAccount();
  const tokensQuery = useApiTokens();
  const createToken = useCreateApiToken();
  const deleteToken = useDeleteApiToken();
  const [iamRoleDrafts, setIamRoleDrafts] = useState<Record<string, string>>({});
  const [configDraft, setConfigDraft] = useState<ConfigDocument | null>(null);
  const [configDirty, setConfigDirty] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditIndex, setWizardEditIndex] = useState<number | null>(null);
  const [pendingFullSyncFrom, setPendingFullSyncFrom] = useState<string | null>(null);
  const [pendingFullSyncAccount, setPendingFullSyncAccount] = useState<string | null>(null);
  const [allowInput, setAllowInput] = useState("");
  const [denyInput, setDenyInput] = useState("");
  const [autoSaveRequested, setAutoSaveRequested] = useState(false);
  const [autoSaveDeferredNoticeShown, setAutoSaveDeferredNoticeShown] = useState(false);
  const [newTokenLabel, setNewTokenLabel] = useState("");
  const [newTokenExpiry, setNewTokenExpiry] = useState<number>(30);
  const [generatedToken, setGeneratedToken] = useState<string>("");
  const [tokenScopeAll, setTokenScopeAll] = useState(true);
  const [tokenAccounts, setTokenAccounts] = useState<string[]>([]);
  const [tokenPermissions, setTokenPermissions] = useState<"read" | "write">("read");
  const [rebuildAccount, setRebuildAccount] = useState("");
  const [rebuildStartDate, setRebuildStartDate] = useState("");
  const [rebuildEndDate, setRebuildEndDate] = useState("");
  const [selectedDbBackup, setSelectedDbBackup] = useState("");
  const [dbImportMode, setDbImportMode] = useState<"replace" | "merge">("replace");
  const [dbImportDryRun, setDbImportDryRun] = useState(true);
  const [dbImportValidation, setDbImportValidation] = useState<Record<string, unknown> | null>(null);
  const [iamSearchTerm, setIamSearchTerm] = useState("");
  const [iamShowOnlyEnabled, setIamShowOnlyEnabled] = useState(false);
  const [iamSortBy, setIamSortBy] = useState<"username" | "email" | "role" | "enabled">("username");
  const [iamSortDir, setIamSortDir] = useState<"asc" | "desc">("asc");
  const [iamPage, setIamPage] = useState(0);
  const [iamRowsPerPage, setIamRowsPerPage] = useState(10);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserUsername, setCreateUserUsername] = useState("");
  const [createUserEmail, setCreateUserEmail] = useState("");
  const [createUserPassword, setCreateUserPassword] = useState("");
  const [createUserRole, setCreateUserRole] = useState("pec-operator");
  const [resetPasswordTarget, setResetPasswordTarget] = useState<IAMUserSummary | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [createGroupName, setCreateGroupName] = useState("");
  const [renameGroupTarget, setRenameGroupTarget] = useState<{ id: string; name: string } | null>(null);
  const [renameGroupName, setRenameGroupName] = useState("");
  const [deleteUserTarget, setDeleteUserTarget] = useState<IAMUserSummary | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<{ id: string; name: string } | null>(null);
  const [groupMembershipTarget, setGroupMembershipTarget] = useState<IAMUserSummary | null>(null);
  const [groupMembershipMode, setGroupMembershipMode] = useState<"add" | "remove" | null>(null);
  const [groupMembershipGroupId, setGroupMembershipGroupId] = useState("");
  const [s3TieringOutput, setS3TieringOutput] = useState<string[]>([]);
  const [s3TieringDirty, setS3TieringDirty] = useState(false);
  const [s3PrunePrecheckOpen, setS3PrunePrecheckOpen] = useState(false);
  const [s3PrunePrecheck, setS3PrunePrecheck] = useState<S3TieringPrunePrecheckResponse | null>(null);
  const [s3TieringForm, setS3TieringForm] = useState({
    backup_mode: "local_tiered_s3" as "local_tiered_s3" | "local_mirror_s3",
    bucket: "",
    region: "",
    prefix: "pec-backups",
    endpoint_url: "",
    local_hot_retention_days: 30,
    min_free_space_gb: 30,
    allow_local_prune: false,
    aws_access_key_id: "",
    aws_secret_access_key: "",
  });

  const configuredAuthProvider = configDraft?.auth?.provider ?? currentAuthProvider;
  const iamAvailable = canManageSettings && configuredAuthProvider === "keycloak";
  const iamUsersQuery = useIamUsers(iamAvailable);
  const iamGroupsQuery = useIamGroups(iamAvailable);
  const createIamUser = useCreateIamUser();
  const updateIamUser = useUpdateIamUser();
  const deleteIamUser = useDeleteIamUser();
  const resetIamUserPassword = useResetIamUserPassword();
  const createIamGroup = useCreateIamGroup();
  const updateIamGroup = useUpdateIamGroup();
  const deleteIamGroup = useDeleteIamGroup();
  const addUserToGroup = useAddUserToGroup();
  const removeUserFromGroup = useRemoveUserFromGroup();
  const s3TieringStatusQuery = useS3TieringStatus(30_000, canManageSettings);
  const runS3TieringAction = useRunS3TieringAction();
  const s3TieringPrunePrecheckQuery = useS3TieringPrunePrecheck();
  const configureS3Tiering = useConfigureS3Tiering();
  const s3TieringStatus = s3TieringStatusQuery.data;
  const s3LastRun = s3TieringStatus?.last_run;

  useEffect(() => {
    if (configQuery.data) {
      const normalized = cloneConfig(configQuery.data);
      normalized.access_control = normalized.access_control ?? { allow: [], deny: [] };
      setConfigDraft(normalized);
      setConfigDirty(false);
      const allow = configQuery.data.access_control?.allow ?? [];
      const deny = configQuery.data.access_control?.deny ?? [];
      setAllowInput(allow.join("\n"));
      setDenyInput(deny.join("\n"));
    }
  }, [configQuery.data]);

  useEffect(() => {
    if (!s3TieringStatus || s3TieringDirty) {
      return;
    }
    setS3TieringForm({
      backup_mode: (s3TieringStatus.backup_mode === "local_mirror_s3" ? "local_mirror_s3" : "local_tiered_s3"),
      bucket: s3TieringStatus.bucket ?? "",
      region: s3TieringStatus.region ?? "",
      prefix: s3TieringStatus.prefix ?? "pec-backups",
      endpoint_url: s3TieringStatus.endpoint_url ?? "",
      local_hot_retention_days: s3TieringStatus.local_hot_retention_days ?? 30,
      min_free_space_gb: s3TieringStatus.min_free_space_gb ?? 30,
      allow_local_prune: Boolean(s3TieringStatus.allow_local_prune),
      aws_access_key_id: "",
      aws_secret_access_key: "",
    });
  }, [s3TieringStatus, s3TieringDirty]);

  const handleThemeChange = (_: unknown, value: string) => {
    if (value) {
      setMode(value as ThemeMode);
    }
  };

  const handleCopy = useCallback(async (value: string, label: string) => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      enqueueNotification({ message: `${label} copiato negli appunti.`, severity: "success", autoHideDuration: 2500 });
    } catch (copyError) {
      enqueueNotification({ message: "Impossibile copiare negli appunti.", severity: "error" });
      console.warn("Clipboard copy failed", copyError);
    }
  }, [enqueueNotification]);

  const schedulerRunning = systemStatus?.scheduler_running ?? false;
  const jobInProgress = systemStatus?.job_in_progress ?? false;
  const sourceSyncEnabledStatus = systemStatus?.source_sync_enabled ?? false;
  const sourceSyncIntervalHoursStatus = systemStatus?.source_sync_interval_hours ?? 6;
  const nextDailyRunLabel = formatDateTime(systemStatus?.next_daily_run_iso ?? systemStatus?.next_run_iso ?? null);
  const nextSourceSyncLabel = formatDateTime(systemStatus?.next_source_sync_iso ?? null);
  const metadataStatus = metadataRebuildStatus.data;
  const dbBackupStatus = databaseBackupStatusQuery.data;
  const dbBackupImportStatus = databaseBackupImportStatusQuery.data;
  const dbBackupEntries = databaseBackupsQuery.data?.backups ?? [];

  useEffect(() => {
    if (!selectedDbBackup && dbBackupEntries.length > 0) {
      setSelectedDbBackup(dbBackupEntries[0].filename);
    }
  }, [selectedDbBackup, dbBackupEntries]);

  useEffect(() => {
    const users = iamUsersQuery.data?.users ?? [];
    if (users.length === 0) {
      setIamRoleDrafts({});
      return;
    }
    setIamRoleDrafts((prev) => {
      const next: Record<string, string> = {};
      users.forEach((user) => {
        const preferred = user.roles.find((role) => ["pec-admin", "pec-operator", "pec-legal"].includes(role)) ?? "pec-operator";
        next[user.id] = prev[user.id] ?? preferred;
      });
      return next;
    });
  }, [iamUsersQuery.data?.users]);

  const filteredIamUsers = useMemo(() => {
    const users = iamUsersQuery.data?.users ?? [];
    const query = iamSearchTerm.trim().toLowerCase();
    return users.filter((user) => {
      if (iamShowOnlyEnabled && !user.enabled) {
        return false;
      }
      if (!query) {
        return true;
      }
      const role = iamRoleDrafts[user.id] ?? user.roles.find((entry) => ["pec-admin", "pec-operator", "pec-legal"].includes(entry)) ?? "";
      const haystack = [
        user.username,
        user.email ?? "",
        role,
        user.groups.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [iamUsersQuery.data?.users, iamSearchTerm, iamShowOnlyEnabled, iamRoleDrafts]);

  const sortedIamUsers = useMemo(() => {
    const users = [...filteredIamUsers];
    users.sort((a, b) => {
      const roleA = iamRoleDrafts[a.id] ?? a.roles.find((entry) => ["pec-admin", "pec-operator", "pec-legal"].includes(entry)) ?? "";
      const roleB = iamRoleDrafts[b.id] ?? b.roles.find((entry) => ["pec-admin", "pec-operator", "pec-legal"].includes(entry)) ?? "";

      let result = 0;
      if (iamSortBy === "username") {
        result = a.username.localeCompare(b.username);
      } else if (iamSortBy === "email") {
        result = (a.email ?? "").localeCompare(b.email ?? "");
      } else if (iamSortBy === "role") {
        result = roleA.localeCompare(roleB);
      } else if (iamSortBy === "enabled") {
        result = Number(a.enabled) - Number(b.enabled);
      }

      return iamSortDir === "asc" ? result : -result;
    });
    return users;
  }, [filteredIamUsers, iamRoleDrafts, iamSortBy, iamSortDir]);

  const pagedIamUsers = useMemo(() => {
    const start = iamPage * iamRowsPerPage;
    return sortedIamUsers.slice(start, start + iamRowsPerPage);
  }, [sortedIamUsers, iamPage, iamRowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedIamUsers.length / iamRowsPerPage) - 1);
    if (iamPage > maxPage) {
      setIamPage(maxPage);
    }
  }, [sortedIamUsers.length, iamRowsPerPage, iamPage]);

  const refreshIamData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["iam-users"] }),
      queryClient.invalidateQueries({ queryKey: ["iam-groups"] })
    ]);
  };

  const runS3Tiering = async (action: S3TieringAction) => {
    if (action === "prune") {
      try {
        const precheck = await s3TieringPrunePrecheckQuery.mutateAsync();
        setS3PrunePrecheck(precheck);
        setS3PrunePrecheckOpen(true);
      } catch (err) {
        enqueueNotification({ message: `Precheck prune non disponibile: ${extractApiError(err)}`, severity: "error" });
      }
      return;
    }

    if (action === "cycle" || action === "unlock-stale") {
      const labels: Record<S3TieringAction, string> = {
        "validate": "validazione",
        "dry-run": "dry-run",
        "offload": "offload",
        "prune": "prune locale",
        "cycle": "ciclo automatico",
        "unlock-stale": "unlock lock stale",
      };
      if (!window.confirm(`Confermi azione S3 tiering: ${labels[action]}?`)) {
        return;
      }
    }

    try {
      const result = await runS3TieringAction.mutateAsync(action);
      setS3TieringOutput(result.output_tail ?? []);
      if (action === "offload" && result.offload_verification?.enabled) {
        const verification = result.offload_verification;
        if (verification.failed_files > 0 || verification.size_mismatches > 0) {
          enqueueNotification({
            message: `Offload con anomalie verifica campione: fail=${verification.failed_files}, mismatch=${verification.size_mismatches}`,
            severity: "warning",
          });
        }
      }
      enqueueNotification({
        message: result.accepted
          ? `Azione S3 tiering completata: ${action}`
          : `Azione S3 tiering terminata con errori: ${action}`,
        severity: result.accepted ? "success" : "warning",
      });
      await queryClient.invalidateQueries({ queryKey: ["system", "s3-tiering", "status"] });
    } catch (err) {
      enqueueNotification({ message: `Azione S3 tiering fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const confirmS3PruneFromPrecheck = async () => {
    if (!s3PrunePrecheck?.allowed) {
      enqueueNotification({ message: "Prerequisiti prune non soddisfatti.", severity: "warning" });
      return;
    }
    setS3PrunePrecheckOpen(false);
    try {
      const result = await runS3TieringAction.mutateAsync("prune");
      setS3TieringOutput(result.output_tail ?? []);
      enqueueNotification({
        message: result.accepted ? "Prune locale completato." : "Prune locale terminato con errori.",
        severity: result.accepted ? "success" : "warning",
      });
      await queryClient.invalidateQueries({ queryKey: ["system", "s3-tiering", "status"] });
    } catch (err) {
      enqueueNotification({ message: `Prune locale fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleSaveS3TieringConfig = async () => {
    if (!canManageS3Tiering) {
      enqueueNotification({ message: "Operazione consentita solo ad amministratori.", severity: "warning" });
      return;
    }

    if (!s3TieringForm.bucket.trim() || !s3TieringForm.region.trim()) {
      enqueueNotification({ message: "Bucket e region sono obbligatori.", severity: "warning" });
      return;
    }

    if (s3TieringForm.local_hot_retention_days < 1 || s3TieringForm.min_free_space_gb < 1) {
      enqueueNotification({ message: "Retention e soglia spazio devono essere >= 1.", severity: "warning" });
      return;
    }

    try {
      await configureS3Tiering.mutateAsync({
        backup_mode: s3TieringForm.backup_mode,
        bucket: s3TieringForm.bucket.trim(),
        region: s3TieringForm.region.trim(),
        prefix: (s3TieringForm.prefix || "pec-backups").trim() || "pec-backups",
        endpoint_url: s3TieringForm.endpoint_url.trim() || undefined,
        local_hot_retention_days: Number(s3TieringForm.local_hot_retention_days),
        min_free_space_gb: Number(s3TieringForm.min_free_space_gb),
        allow_local_prune: Boolean(s3TieringForm.allow_local_prune),
        aws_access_key_id: s3TieringForm.aws_access_key_id.trim() || undefined,
        aws_secret_access_key: s3TieringForm.aws_secret_access_key.trim() || undefined,
      });
      setS3TieringDirty(false);
      setS3TieringOutput([]);
      enqueueNotification({ message: "Configurazione S3 tiering aggiornata.", severity: "success" });
      await queryClient.invalidateQueries({ queryKey: ["system", "s3-tiering", "status"] });
      await queryClient.invalidateQueries({ queryKey: ["config"] });
    } catch (err) {
      enqueueNotification({ message: `Aggiornamento configurazione S3 tiering fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const openCreateIamUserDialog = () => {
    setCreateUserUsername("");
    setCreateUserEmail("");
    setCreateUserPassword("");
    setCreateUserRole("pec-operator");
    setCreateUserOpen(true);
  };

  const handleCreateIamUser = async () => {
    const username = createUserUsername.trim();
    const password = createUserPassword.trim();
    const email = createUserEmail.trim();
    if (!username || !password) {
      enqueueNotification({ message: "Username e password sono obbligatori.", severity: "warning" });
      return;
    }
    try {
      await createIamUser.mutateAsync({
        username,
        password,
        email: email || undefined,
        enabled: true,
        roles: [createUserRole]
      });
      enqueueNotification({ message: `Utente ${username} creato.`, severity: "success" });
      setCreateUserOpen(false);
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Creazione utente fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleSaveIamUserRole = async (user: IAMUserSummary) => {
    const selectedRole = iamRoleDrafts[user.id] ?? "pec-operator";
    try {
      await updateIamUser.mutateAsync({
        userId: user.id,
        update: {
          enabled: user.enabled,
          roles: [selectedRole]
        }
      });
      enqueueNotification({ message: `Ruolo aggiornato per ${user.username}.`, severity: "success" });
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Aggiornamento ruolo fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleToggleIamUserEnabled = async (user: IAMUserSummary, enabled: boolean) => {
    try {
      await updateIamUser.mutateAsync({
        userId: user.id,
        update: {
          enabled,
          roles: [iamRoleDrafts[user.id] ?? "pec-operator"]
        }
      });
      enqueueNotification({ message: `Stato aggiornato per ${user.username}.`, severity: "success" });
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Aggiornamento stato fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const openResetIamPasswordDialog = (user: IAMUserSummary) => {
    setResetPasswordTarget(user);
    setResetPasswordValue("");
  };

  const handleResetIamPassword = async () => {
    const user = resetPasswordTarget;
    const password = resetPasswordValue.trim();
    if (!user || !password) {
      enqueueNotification({ message: "Password obbligatoria.", severity: "warning" });
      return;
    }
    try {
      await resetIamUserPassword.mutateAsync({ userId: user.id, password, temporary: false });
      enqueueNotification({ message: `Password reimpostata per ${user.username}.`, severity: "success" });
      setResetPasswordTarget(null);
      setResetPasswordValue("");
    } catch (err) {
      enqueueNotification({ message: `Reset password fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleDeleteIamUser = async (user: IAMUserSummary) => {
    try {
      await deleteIamUser.mutateAsync(user.id);
      enqueueNotification({ message: `Utente ${user.username} eliminato.`, severity: "success" });
      setDeleteUserTarget(null);
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Eliminazione utente fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const openDeleteIamUserDialog = (user: IAMUserSummary) => {
    setDeleteUserTarget(user);
  };

  const openCreateIamGroupDialog = () => {
    setCreateGroupName("");
    setCreateGroupOpen(true);
  };

  const handleCreateIamGroup = async () => {
    const name = createGroupName.trim();
    if (!name) {
      return;
    }
    try {
      await createIamGroup.mutateAsync({ name });
      enqueueNotification({ message: `Gruppo ${name} creato.`, severity: "success" });
      setCreateGroupOpen(false);
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Creazione gruppo fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const openRenameIamGroupDialog = (groupId: string, currentName: string) => {
    setRenameGroupTarget({ id: groupId, name: currentName });
    setRenameGroupName(currentName);
  };

  const handleRenameIamGroup = async () => {
    if (!renameGroupTarget) {
      return;
    }
    const updatedName = renameGroupName.trim();
    if (!updatedName || updatedName === renameGroupTarget.name) {
      return;
    }
    try {
      await updateIamGroup.mutateAsync({ groupId: renameGroupTarget.id, name: updatedName });
      enqueueNotification({ message: `Gruppo rinominato in ${updatedName}.`, severity: "success" });
      setRenameGroupTarget(null);
      setRenameGroupName("");
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Rinomina gruppo fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleDeleteIamGroup = async (groupId: string, groupName: string) => {
    try {
      await deleteIamGroup.mutateAsync(groupId);
      enqueueNotification({ message: `Gruppo ${groupName} eliminato.`, severity: "success" });
      setDeleteGroupTarget(null);
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Eliminazione gruppo fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const openDeleteIamGroupDialog = (groupId: string, groupName: string) => {
    setDeleteGroupTarget({ id: groupId, name: groupName });
  };

  const openAssignUserGroupDialog = (user: IAMUserSummary) => {
    setGroupMembershipTarget(user);
    setGroupMembershipMode("add");
    setGroupMembershipGroupId("");
  };

  const openRemoveUserGroupDialog = (user: IAMUserSummary) => {
    setGroupMembershipTarget(user);
    setGroupMembershipMode("remove");
    setGroupMembershipGroupId("");
  };

  const handleAssignUserGroup = async () => {
    const user = groupMembershipTarget;
    const groupId = groupMembershipGroupId;
    if (!user || !groupId) {
      enqueueNotification({ message: "Seleziona un gruppo.", severity: "warning" });
      return;
    }
    const group = (iamGroupsQuery.data?.groups ?? []).find((item) => item.id === groupId);
    if (!group) {
      enqueueNotification({ message: "Gruppo non trovato.", severity: "warning" });
      return;
    }
    try {
      await addUserToGroup.mutateAsync({ userId: user.id, groupId: group.id });
      enqueueNotification({ message: `Gruppo ${group.name} assegnato a ${user.username}.`, severity: "success" });
      setGroupMembershipTarget(null);
      setGroupMembershipMode(null);
      setGroupMembershipGroupId("");
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Assegnazione gruppo fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleRemoveUserGroup = async () => {
    const user = groupMembershipTarget;
    const groupId = groupMembershipGroupId;
    if (!user || !groupId) {
      enqueueNotification({ message: "Seleziona un gruppo.", severity: "warning" });
      return;
    }
    const group = (iamGroupsQuery.data?.groups ?? []).find((item) => item.id === groupId);
    if (!group) {
      enqueueNotification({ message: "Gruppo non trovato.", severity: "warning" });
      return;
    }
    try {
      await removeUserFromGroup.mutateAsync({ userId: user.id, groupId: group.id });
      enqueueNotification({ message: `Gruppo ${group.name} rimosso da ${user.username}.`, severity: "success" });
      setGroupMembershipTarget(null);
      setGroupMembershipMode(null);
      setGroupMembershipGroupId("");
      await refreshIamData();
    } catch (err) {
      enqueueNotification({ message: `Rimozione gruppo fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const updateDraft = useCallback((updater: (config: ConfigDocument) => void) => {
    setConfigDraft((prev) => {
      if (!prev) {
        return prev;
      }
      const next = cloneConfig(prev);
      updater(next);
      setConfigDirty(true);
      return next;
    });
  }, []);

  const handleAddAccount = () => {
    updateDraft((draft) => {
      draft.accounts.push(defaultAccount());
    });
  };

  const handleCreateToken = async () => {
    setGeneratedToken("");
    try {
      const created = await createToken.mutateAsync({
        label: newTokenLabel.trim() || undefined,
        expires_in_days: newTokenExpiry,
        accounts: tokenScopeAll ? undefined : tokenAccounts,
        permissions: tokenPermissions === "write" ? ["read", "write"] : ["read"]
      });
      setGeneratedToken(created.token);
      enqueueNotification({ message: "Token API generato.", severity: "success" });
      setNewTokenLabel("");
      await queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    } catch (err) {
      enqueueNotification({ message: `Impossibile generare il token: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleDeleteToken = async (id: string) => {
    if (!window.confirm("Eliminare definitivamente questo token API? L'accesso associato sarà revocato.")) {
      return;
    }
    try {
      await deleteToken.mutateAsync(id);
      enqueueNotification({ message: "Token eliminato.", severity: "success" });
      await queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    } catch (err) {
      enqueueNotification({ message: `Eliminazione token fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleWizardComplete = (account: AccountConfig, sync?: { fullSyncFrom?: string }) => {
    const accountName = account.username.trim();
    const isEdit = wizardEditIndex !== null;
    updateDraft((draft) => {
      if (wizardEditIndex !== null && draft.accounts[wizardEditIndex]) {
        draft.accounts[wizardEditIndex] = account;
      } else {
        draft.accounts.push(account);
      }
    });
    if (sync) {
      setPendingFullSyncFrom(sync.fullSyncFrom ?? "");
      setPendingFullSyncAccount(accountName || null);
      setAutoSaveRequested(true);
    }
    enqueueNotification({
      message: isEdit
        ? `Casella ${account.username || "PEC"} aggiornata tramite wizard.`
        : `Casella ${account.username || "PEC"} aggiunta tramite wizard.`,
      severity: "success"
    });
    setWizardOpen(false);
    setWizardEditIndex(null);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setWizardEditIndex(null);
  };

  const wizardInitialAccount = useMemo(() => {
    if (wizardEditIndex !== null && configDraft?.accounts[wizardEditIndex]) {
      return configDraft.accounts[wizardEditIndex];
    }
    return defaultAccount();
  }, [wizardEditIndex, configDraft]);

  const handleRemoveAccount = (index: number) => {
    const account = configDraft?.accounts[index];
    if (!account) return;
    if (!window.confirm(`Rimuovere la casella ${account.username}? Verrà eliminato anche l'archivio locale.`)) {
      return;
    }

    deleteAccount.mutate(
      { account: account.username },
      {
        onError: (err) =>
          enqueueNotification({
            message: `Eliminazione archivio fallita: ${extractApiError(err)}`,
            severity: "error"
          })
      }
    );

    updateDraft((draft) => {
      draft.accounts.splice(index, 1);
      setConfigDirty(true);
    });
  };

  const handleRebuildMetadataIndex = async () => {
    try {
      const accountValue = rebuildAccount.trim();
      await rebuildMetadataIndex.mutateAsync({
        account: accountValue || undefined,
        start_date: rebuildStartDate || undefined,
        end_date: rebuildEndDate || undefined
      });
      enqueueNotification({
        message: "Rebuild indice metadati avviato in background.",
        severity: "success"
      });
    } catch (err) {
      enqueueNotification({
        message: `Rebuild indice fallito: ${extractApiError(err)}`,
        severity: "error"
      });
    }
  };

  const handleExportDatabaseBackup = async () => {
    try {
      const result = await exportDatabaseBackup.mutateAsync();
      enqueueNotification({
        message: `Export backup database avviato (backend: ${result.backend}).`,
        severity: "success"
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["db-backup-status"] }),
        queryClient.invalidateQueries({ queryKey: ["db-backup-list"] })
      ]);
    } catch (err) {
      enqueueNotification({
        message: `Export backup database fallito: ${extractApiError(err)}`,
        severity: "error"
      });
    }
  };

  const handleDownloadDatabaseBackup = async (filename: string) => {
    try {
      const { data } = await apiClient.get(
        `/v1/system/db-backup/download/${encodeURIComponent(filename)}`,
        { responseType: "blob" }
      );
      const objectUrl = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      enqueueNotification({ message: `Download avviato: ${filename}`, severity: "success", autoHideDuration: 2500 });
    } catch (err) {
      enqueueNotification({
        message: `Download backup fallito: ${extractApiError(err)}`,
        severity: "error"
      });
    }
  };

  const handleValidateDatabaseBackup = async () => {
    if (!selectedDbBackup) {
      enqueueNotification({ message: "Seleziona un backup da validare.", severity: "warning" });
      return;
    }
    try {
      const payload = await validateDatabaseBackupImport.mutateAsync({ filename: selectedDbBackup });
      setDbImportValidation(payload as unknown as Record<string, unknown>);
      enqueueNotification({ message: "Validazione backup completata.", severity: "success" });
    } catch (err) {
      setDbImportValidation(null);
      enqueueNotification({ message: `Validazione backup fallita: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleImportDatabaseBackup = async () => {
    if (!selectedDbBackup) {
      enqueueNotification({ message: "Seleziona un backup da importare.", severity: "warning" });
      return;
    }
    try {
      await importDatabaseBackup.mutateAsync({
        filename: selectedDbBackup,
        mode: dbImportMode,
        dry_run: dbImportDryRun
      });
      enqueueNotification({
        message: dbImportDryRun ? "Dry-run import avviato." : "Import backup avviato in background.",
        severity: "success"
      });
      await queryClient.invalidateQueries({ queryKey: ["db-backup-import-status"] });
    } catch (err) {
      enqueueNotification({ message: `Import backup fallito: ${extractApiError(err)}`, severity: "error" });
    }
  };

  const handleAccountChange = (index: number, field: keyof AccountConfig, value: string) => {
    updateDraft((draft) => {
      const account = draft.accounts[index];
      if (!account) {
        return;
      }
      if (field === "port") {
        account.port = Number(value) || 993;
      } else if (field === "folders") {
        account.folders = value
          .split(/[,\n]/)
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0);
      } else {
        (account as Record<string, unknown>)[field] = value;
      }
    });
  };

  const handleBackupModeChange = (mode: string) => {
    updateDraft((draft) => {
      draft.backup_mode = mode;
      if (mode !== "local_mirror_s3") {
        draft.s3 = undefined;
      } else {
        draft.s3 = draft.s3 ?? defaultS3Config();
      }
    });
  };

  const handleNotificationsToggle = (enabled: boolean) => {
    updateDraft((draft) => {
      draft.notifications.enabled = enabled;
      if (!enabled) {
        draft.notifications.smtp = draft.notifications.smtp ?? {};
      }
    });
  };

  const handleNotificationsRecipients = (value: string) => {
    updateDraft((draft) => {
      draft.notifications.recipients = value
        .split(/[,\n]/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
    });
  };

  const handleNotificationSmtpChange = (field: keyof NotificationSmtpConfig, value: string | number | boolean) => {
    updateDraft((draft) => {
      draft.notifications.smtp = draft.notifications.smtp ?? {};
      (draft.notifications.smtp as Record<string, unknown>)[field] = value;
    });
  };

  const handleAllowChange = (value: string) => {
    setAllowInput(value);
    updateDraft((draft) => {
      draft.access_control = draft.access_control ?? { allow: [], deny: [] };
      draft.access_control.allow = parseListInput(value);
    });
  };

  const handleDenyChange = (value: string) => {
    setDenyInput(value);
    updateDraft((draft) => {
      draft.access_control = draft.access_control ?? { allow: [], deny: [] };
      draft.access_control.deny = parseListInput(value);
    });
  };

  const handleAuthToggle = (enabled: boolean) => {
    updateDraft((draft) => {
      draft.auth.enabled = enabled;
      if (draft.auth.provider !== "keycloak") {
        draft.auth.users = draft.auth.users ?? [];
        if (enabled && draft.auth.users.length === 0) {
          draft.auth.users.push(defaultAuthUser());
        }
      }
    });
  };

  const handleAuthUserChange = (index: number, field: keyof AuthUserConfig, value: string) => {
    updateDraft((draft) => {
      if (draft.auth.provider === "keycloak") {
        return;
      }
      const users = draft.auth.users ?? [];
      if (!users[index]) {
        return;
      }
      (users[index] as Record<string, string>)[field] = value;
      draft.auth.users = users;
    });
  };

  const handleRemoveAuthUser = (index: number) => {
    updateDraft((draft) => {
      if (draft.auth.provider === "keycloak") {
        return;
      }
      const users = draft.auth.users ?? [];
      users.splice(index, 1);
      draft.auth.users = users;
    });
  };

  const handleAddAuthUser = () => {
    updateDraft((draft) => {
      if (draft.auth.provider === "keycloak") {
        return;
      }
      const users = draft.auth.users ?? [];
      users.push(defaultAuthUser());
      draft.auth.users = users;
    });
  };

  const handleAuthProviderChange = (provider: "local" | "keycloak") => {
    updateDraft((draft) => {
      draft.auth.provider = provider;
      if (provider === "keycloak") {
        draft.auth.users = [];
        draft.auth.secret_key = undefined;
        draft.auth.algorithm = undefined;
        draft.auth.token_expiry_minutes = undefined;
        draft.auth.issuer_url = draft.auth.issuer_url ?? "";
        draft.auth.client_id = draft.auth.client_id ?? "";
        draft.auth.audience = draft.auth.audience ?? "";
        draft.auth.jwks_url = draft.auth.jwks_url ?? "";
        draft.auth.jwks_cache_seconds = draft.auth.jwks_cache_seconds ?? 3_600;
      } else {
        draft.auth.secret_key = draft.auth.secret_key ?? "";
        draft.auth.algorithm = draft.auth.algorithm ?? "HS256";
        draft.auth.token_expiry_minutes = draft.auth.token_expiry_minutes ?? 60;
        draft.auth.users = draft.auth.users && draft.auth.users.length > 0 ? draft.auth.users : [defaultAuthUser()];
        draft.auth.issuer_url = undefined;
        draft.auth.client_id = undefined;
        draft.auth.audience = undefined;
        draft.auth.jwks_url = undefined;
        draft.auth.jwks_cache_seconds = undefined;
      }
    });
  };

  const authProvider = configDraft?.auth?.provider ?? "local";
  const isKeycloakProvider = authProvider === "keycloak";
  const s3Enabled = configDraft?.backup_mode === "local_mirror_s3";

  const recipientsPreview = useMemo(() => {
    if (!configDraft?.notifications?.recipients || configDraft.notifications.recipients.length === 0) {
      return "";
    }
    return configDraft.notifications.recipients.join("\n");
  }, [configDraft?.notifications?.recipients]);

  const handleSaveConfig = async () => {
    if (!configDraft) {
      return;
    }

    if (jobInProgress) {
      enqueueNotification({
        message: "Job di archiviazione in corso: il salvataggio configurazione verra eseguito al termine.",
        severity: "info"
      });
      return;
    }

    const payload = cloneConfig(configDraft);

    if (payload.backup_mode !== "local_mirror_s3") {
      delete payload.s3;
    } else if (payload.s3) {
      const cleanedS3: Record<string, unknown> = { ...payload.s3 };
      Object.entries(payload.s3).forEach(([key, value]) => {
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed.length === 0) {
            delete cleanedS3[key];
          } else {
            cleanedS3[key] = trimmed;
          }
        }
      });
      payload.s3 = cleanedS3 as S3Config;
    }

    payload.notifications.recipients = (payload.notifications.recipients ?? []).filter((entry) => entry.trim().length > 0);
    if (payload.notifications.smtp) {
      const cleanedSmtp: Record<string, unknown> = {};
      Object.entries(payload.notifications.smtp).forEach(([key, value]) => {
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed.length > 0) {
            cleanedSmtp[key] = trimmed;
          }
        } else if (value !== undefined) {
          cleanedSmtp[key] = value;
        }
      });
      if (Object.keys(cleanedSmtp).length === 0) {
        delete payload.notifications.smtp;
      } else {
        payload.notifications.smtp = cleanedSmtp as NotificationSmtpConfig;
      }
    }

    payload.access_control = payload.access_control ?? { allow: [], deny: [] };
    payload.access_control.allow = parseListInput(allowInput);
    payload.access_control.deny = parseListInput(denyInput);

    payload.accounts = payload.accounts.map((account) => {
      const trimmedLabel = account.label?.trim() ?? "";
      const cleaned: AccountConfig = {
        ...account,
        username: account.username.trim(),
        host: account.host.trim(),
        folders: account.folders.map((folder) => folder.trim()).filter((folder) => folder.length > 0)
      };
      if (trimmedLabel.length > 0) {
        cleaned.label = trimmedLabel;
      } else {
        delete cleaned.label;
      }
      return cleaned;
    });

    const provider = (payload.auth.provider ?? "local") as "local" | "keycloak";
    payload.auth.provider = provider;

    if (provider === "keycloak") {
      const issuer = (payload.auth.issuer_url ?? "").trim();
      const clientId = (payload.auth.client_id ?? "").trim();
      const audience = (payload.auth.audience ?? "").trim();
      const jwksUrl = (payload.auth.jwks_url ?? "").trim();
      const jwksCacheRaw =
        typeof payload.auth.jwks_cache_seconds === "number"
          ? payload.auth.jwks_cache_seconds
          : Number(payload.auth.jwks_cache_seconds ?? 3_600);
      const jwksCache = Number.isFinite(jwksCacheRaw) && jwksCacheRaw > 0 ? Math.floor(jwksCacheRaw) : 3_600;

      payload.auth.issuer_url = issuer.length > 0 ? issuer : undefined;
      payload.auth.client_id = clientId.length > 0 ? clientId : undefined;
      payload.auth.audience = audience.length > 0 ? audience : undefined;
      payload.auth.jwks_url = jwksUrl.length > 0 ? jwksUrl : undefined;
      payload.auth.jwks_cache_seconds = jwksCache;
      payload.auth.users = [];
      payload.auth.secret_key = undefined;
      payload.auth.algorithm = undefined;
      payload.auth.token_expiry_minutes = undefined;
    } else {
      const trimmedSecret = (payload.auth.secret_key ?? "").trim();
      const trimmedAlgorithm = (payload.auth.algorithm ?? "HS256").trim();
      const expiry = Number(payload.auth.token_expiry_minutes ?? 60) || 60;

      payload.auth.secret_key = trimmedSecret;
      payload.auth.algorithm = trimmedAlgorithm.length > 0 ? trimmedAlgorithm : "HS256";
      payload.auth.token_expiry_minutes = expiry;
      payload.auth.issuer_url = undefined;
      payload.auth.client_id = undefined;
      payload.auth.audience = undefined;
      payload.auth.jwks_url = undefined;
      payload.auth.jwks_cache_seconds = undefined;

      if (payload.auth.enabled) {
        payload.auth.users = (payload.auth.users ?? []).map((user) => ({
          username: user.username.trim(),
          password: user.password
        }));
      } else {
        payload.auth.users = payload.auth.users ?? [];
      }
    }

    try {
      await validateConfig.mutateAsync(payload);
      const saved = await updateConfig.mutateAsync(payload);
      const normalized = cloneConfig(saved);
      normalized.access_control = normalized.access_control ?? { allow: [], deny: [] };
      setConfigDraft(normalized);
      setAllowInput((normalized.access_control?.allow ?? []).join("\n"));
      setDenyInput((normalized.access_control?.deny ?? []).join("\n"));
      setConfigDirty(false);
      setAutoSaveRequested(false);
      setAutoSaveDeferredNoticeShown(false);
      enqueueNotification({ message: "Configurazione salvata con successo.", severity: "success" });

      if (pendingFullSyncFrom !== null && pendingFullSyncAccount) {
        runAccount.mutate(
          pendingFullSyncFrom
            ? { account: pendingFullSyncAccount, start_date: pendingFullSyncFrom, full_history: true }
            : { account: pendingFullSyncAccount, full_history: true },
          {
            onSuccess: () => {
              enqueueNotification({ message: `Avvio storico in background per ${pendingFullSyncAccount}.`, severity: "info" });
              setPendingFullSyncFrom(null);
              setPendingFullSyncAccount(null);
            },
            onError: (err) => {
              enqueueNotification({ message: `Avvio storico fallito per ${pendingFullSyncAccount}: ${extractApiError(err)}`, severity: "error" });
              setPendingFullSyncFrom(null);
              setPendingFullSyncAccount(null);
            }
          }
        );
      } else if (pendingFullSyncFrom !== null) {
        setPendingFullSyncFrom(null);
        setPendingFullSyncAccount(null);
      }
    } catch (saveError) {
      enqueueNotification({
        message: `Salvataggio configurazione non riuscito: ${extractApiError(saveError)}`,
        severity: "error"
      });
      setAutoSaveRequested(false);
      setAutoSaveDeferredNoticeShown(false);
    }
  };

  useEffect(() => {
    if (!autoSaveRequested || !configDirty || updateConfig.isPending || validateConfig.isPending) {
      return;
    }

    if (jobInProgress) {
      if (!autoSaveDeferredNoticeShown) {
        enqueueNotification({
          message: "Salvataggio automatico rimandato: e in corso un job di archiviazione.",
          severity: "info",
          autoHideDuration: 3500
        });
        setAutoSaveDeferredNoticeShown(true);
      }
      return;
    }

    if (autoSaveDeferredNoticeShown) {
      setAutoSaveDeferredNoticeShown(false);
    }

    handleSaveConfig();
  }, [
    autoSaveRequested,
    autoSaveDeferredNoticeShown,
    configDirty,
    enqueueNotification,
    jobInProgress,
    updateConfig.isPending,
    validateConfig.isPending
  ]);

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Typography component="h1" variant="h4" gutterBottom sx={{ mb: 0 }}>
            Impostazioni
          </Typography>
          <Chip label={`Versione ${__APP_VERSION__}`} color="default" size="small" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Gestisci la configurazione del servizio PEC Archiver e personalizza le preferenze locali della console.
        </Typography>
      </Box>

      <Card>
        <CardHeader
          title="Utenti e gruppi (Keycloak)"
          subheader="Gestione amministrativa utenti e gruppi per accesso alla console"
          action={
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={refreshIamData} disabled={!iamAvailable}>
                Aggiorna
              </Button>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreateIamUserDialog} disabled={!iamAvailable}>
                Nuovo utente
              </Button>
            </Stack>
          }
        />
        <CardContent>
          {!canManageSettings ? (
            <Alert severity="warning">Permessi insufficienti per gestire utenti e gruppi.</Alert>
          ) : configuredAuthProvider !== "keycloak" ? (
            <Alert severity="info">La gestione utenti/gruppi è disponibile solo quando il provider autenticazione è Keycloak.</Alert>
          ) : iamUsersQuery.isLoading || iamGroupsQuery.isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Caricamento dati IAM...</Typography>
            </Stack>
          ) : iamUsersQuery.isError || iamGroupsQuery.isError ? (
            <Alert severity="error">
              Errore caricamento IAM: {formatIamLoadError(iamUsersQuery.error ?? iamGroupsQuery.error)}
            </Alert>
          ) : (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="subtitle1">Utenti</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Cerca utente"
                    value={iamSearchTerm}
                    onChange={(event) => setIamSearchTerm(event.target.value)}
                    placeholder="username, email, ruolo, gruppo"
                    size="small"
                    fullWidth
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={iamShowOnlyEnabled}
                        onChange={(event) => setIamShowOnlyEnabled(event.target.checked)}
                      />
                    }
                    label="Solo attivi"
                  />
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="iam-sort-by-label">Ordina per</InputLabel>
                    <Select
                      labelId="iam-sort-by-label"
                      label="Ordina per"
                      value={iamSortBy}
                      onChange={(event) => setIamSortBy(event.target.value as "username" | "email" | "role" | "enabled")}
                    >
                      <MenuItem value="username">Username</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="role">Ruolo</MenuItem>
                      <MenuItem value="enabled">Stato</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="iam-sort-dir-label">Direzione</InputLabel>
                    <Select
                      labelId="iam-sort-dir-label"
                      label="Direzione"
                      value={iamSortDir}
                      onChange={(event) => setIamSortDir(event.target.value as "asc" | "desc")}
                    >
                      <MenuItem value="asc">Crescente</MenuItem>
                      <MenuItem value="desc">Decrescente</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                {(filteredIamUsers ?? []).length === 0 ? (
                  <Alert severity="info">Nessun utente trovato nel realm corrente.</Alert>
                ) : (
                  <>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Username</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Ruolo</TableCell>
                          <TableCell>Gruppi</TableCell>
                          <TableCell>Attivo</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedIamUsers.map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" fontWeight={600}>{user.username}</Typography>
                                <Chip
                                  size="small"
                                  label={iamRoleDrafts[user.id] ?? "pec-operator"}
                                  color={(iamRoleDrafts[user.id] ?? "pec-operator") === "pec-admin" ? "warning" : "default"}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell>{user.email || "-"}</TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <Select
                                size="small"
                                value={iamRoleDrafts[user.id] ?? "pec-operator"}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setIamRoleDrafts((prev) => ({ ...prev, [user.id]: value }));
                                }}
                              >
                                <MenuItem value="pec-admin">pec-admin</MenuItem>
                                <MenuItem value="pec-operator">pec-operator</MenuItem>
                                <MenuItem value="pec-legal">pec-legal</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>{user.groups.length > 0 ? user.groups.join(", ") : "-"}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={user.enabled ? "Attivo" : "Disattivato"}
                                  color={user.enabled ? "success" : "default"}
                                  variant={user.enabled ? "filled" : "outlined"}
                                />
                                <Switch
                                  size="small"
                                  checked={user.enabled}
                                  onChange={(event) => handleToggleIamUserEnabled(user, event.target.checked)}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => handleSaveIamUserRole(user)}>
                                  Salva ruolo
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => openAssignUserGroupDialog(user)}>
                                  + Gruppo
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => openRemoveUserGroupDialog(user)}>
                                  - Gruppo
                                </Button>
                                <Button size="small" variant="outlined" onClick={() => openResetIamPasswordDialog(user)}>
                                  Password
                                </Button>
                                <IconButton color="error" size="small" onClick={() => openDeleteIamUserDialog(user)}>
                                  <DeleteRoundedIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ pt: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {(sortedIamUsers.length === 0)
                        ? "0 risultati"
                        : `Risultati ${(iamPage * iamRowsPerPage) + 1}-${Math.min((iamPage + 1) * iamRowsPerPage, sortedIamUsers.length)} di ${sortedIamUsers.length}`}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="iam-rows-label">Righe</InputLabel>
                        <Select
                          labelId="iam-rows-label"
                          label="Righe"
                          value={String(iamRowsPerPage)}
                          onChange={(event) => {
                            setIamRowsPerPage(Number(event.target.value));
                            setIamPage(0);
                          }}
                        >
                          {[10, 25, 50].map((option) => (
                            <MenuItem key={option} value={String(option)}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setIamPage((prev) => Math.max(0, prev - 1))}
                        disabled={iamPage === 0}
                      >
                        Prec
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setIamPage((prev) => prev + 1)}
                        disabled={(iamPage + 1) * iamRowsPerPage >= sortedIamUsers.length}
                      >
                        Succ
                      </Button>
                    </Stack>
                  </Stack>
                  </>
                )}
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1">Gruppi</Typography>
                  <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={openCreateIamGroupDialog}>
                    Nuovo gruppo
                  </Button>
                </Stack>
                {(iamGroupsQuery.data?.groups ?? []).length === 0 ? (
                  <Alert severity="info">Nessun gruppo trovato.</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nome</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(iamGroupsQuery.data?.groups ?? []).map((group) => (
                          <TableRow key={group.id} hover>
                            <TableCell>{group.name}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => openRenameIamGroupDialog(group.id, group.name)}>
                                  Rinomina
                                </Button>
                                <IconButton color="error" size="small" onClick={() => openDeleteIamGroupDialog(group.id, group.name)}>
                                  <DeleteRoundedIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Configurazione servizio"
          subheader="Imposta caselle PEC, pianificazione, modalità di backup e accesso all'API."
        />
        <Divider />
        <CardContent>
          {configQuery.isLoading && !configDraft ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Caricamento configurazione in corso...
              </Typography>
            </Stack>
          ) : configQuery.isError ? (
            <Alert severity="error">
              Impossibile caricare la configurazione: {extractApiError(configQuery.error)}
            </Alert>
          ) : configDraft ? (
            <Stack spacing={4}>
              <Stack spacing={2}>
                <Typography variant="h6">Generale</Typography>
                <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Percorso archivio"
                      value={configDraft.base_path}
                      onChange={(event) => updateDraft((draft) => { draft.base_path = event.target.value; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 3 }}>
                    <TextField
                      label="Processi paralleli"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={configDraft.concurrency}
                      onChange={(event) => updateDraft((draft) => { draft.concurrency = Number(event.target.value) || 1; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 3 }}>
                    <TextField
                      label="Orario scheduler"
                      type="time"
                      value={configDraft.scheduler?.run_time ?? "01:00"}
                      onChange={(event) => updateDraft((draft) => { draft.scheduler.run_time = event.target.value; })}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Stack spacing={1.5} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configDraft.scheduler?.source_sync_enabled ?? false}
                        onChange={(event) => updateDraft((draft) => {
                          draft.scheduler.source_sync_enabled = event.target.checked;
                        })}
                      />
                    }
                    label="Abilita source-sync infragiornaliero"
                  />
                  <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                    <Grid size={{ xs: 1, md: 4 }}>
                      <FormControl fullWidth size="small" disabled={!(configDraft.scheduler?.source_sync_enabled ?? false)}>
                        <InputLabel id="source-sync-interval-label">Frequenza source-sync</InputLabel>
                        <Select
                          labelId="source-sync-interval-label"
                          label="Frequenza source-sync"
                          value={String(configDraft.scheduler?.source_sync_interval_hours ?? 6)}
                          onChange={(event) => updateDraft((draft) => {
                            draft.scheduler.source_sync_interval_hours = Number(event.target.value);
                          })}
                        >
                          {sourceSyncIntervalOptions.map((hours) => (
                            <MenuItem key={hours} value={String(hours)}>
                              {hours === 1 ? "Ogni ora" : `Ogni ${hours} ore`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary">
                    Questa opzione aumenta solo la frequenza di download dalle caselle sorgente. La logica di archiviazione per giorni resta invariata.
                  </Typography>
                </Stack>

                <FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Modalità di backup
                  </Typography>
                  <RadioGroup
                    row
                    value={configDraft.backup_mode}
                    onChange={(event) => handleBackupModeChange(event.target.value)}
                  >
                    <FormControlLabel value="local_mirror" control={<Radio />} label="Cartella locale" />
                    <FormControlLabel value="local_mirror_s3" control={<Radio />} label="Cartella locale + S3" />
                  </RadioGroup>
                </FormControl>

                {s3Enabled ? (
                  <Stack spacing={2} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle1">Configurazione S3</Typography>
                    <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                      <Grid size={{ xs: 1, md: 4 }}>
                        <TextField
                          label="Bucket"
                          value={configDraft.s3?.bucket ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.bucket = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 4 }}>
                        <TextField
                          label="Regione"
                          value={configDraft.s3?.region ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.region = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 4 }}>
                        <TextField
                          label="Prefisso (opzionale)"
                          value={configDraft.s3?.prefix ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.prefix = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 6 }}>
                        <TextField
                          label="Endpoint personalizzato"
                          value={configDraft.s3?.endpoint_url ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.endpoint_url = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 3 }}>
                        <TextField
                          label="Access Key"
                          value={configDraft.s3?.aws_access_key_id ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.aws_access_key_id = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 3 }}>
                        <TextField
                          label="Secret Key"
                          type="password"
                          value={configDraft.s3?.aws_secret_access_key ?? ""}
                          onChange={(event) => updateDraft((draft) => {
                            draft.s3 = draft.s3 ?? defaultS3Config();
                            draft.s3.aws_secret_access_key = event.target.value;
                          })}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : null}
              </Stack>

              <Stack spacing={2}>
                <Typography variant="h6">Caselle PEC</Typography>
                <Stack spacing={2}>
                  {configDraft.accounts.map((account, index) => (
                    <Stack
                      key={`${account.username || "nuovo"}-${index}`}
                      spacing={2}
                      sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2 }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          Account {account.username || `#${index + 1}`}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AutoAwesomeRoundedIcon />}
                          onClick={() => {
                            setWizardEditIndex(index);
                            setWizardOpen(true);
                          }}
                        >
                          Modifica guidata
                        </Button>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveAccount(index)}
                          disabled={configDraft.accounts.length === 1}
                        >
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Nome visualizzato"
                            value={account.label ?? ""}
                            onChange={(event) => handleAccountChange(index, "label", event.target.value)}
                            helperText="Usato nelle liste a tendina"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Indirizzo"
                            value={account.username}
                            onChange={(event) => handleAccountChange(index, "username", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Password"
                            type="password"
                            value={account.password}
                            onChange={(event) => handleAccountChange(index, "password", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="Server IMAP"
                            value={account.host}
                            onChange={(event) => handleAccountChange(index, "host", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 2 }}>
                          <TextField
                            label="Porta"
                            type="number"
                            value={account.port ?? 993}
                            onChange={(event) => handleAccountChange(index, "port", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 12 }}>
                          <TextField
                            label="Cartelle da archiviare"
                            helperText="Separare con virgola o nuova linea"
                            value={account.folders.join(", ")}
                            onChange={(event) => handleAccountChange(index, "folders", event.target.value)}
                            fullWidth
                            multiline
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  ))}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<AutoAwesomeRoundedIcon />}
                      onClick={() => {
                        setWizardEditIndex(null);
                        setWizardOpen(true);
                      }}
                    >
                      Configura guidata casella
                    </Button>
                    <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleAddAccount}>
                      Aggiungi casella PEC
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<SaveRoundedIcon />}
                      onClick={handleSaveConfig}
                      disabled={!configDraft || updateConfig.isPending || validateConfig.isPending || !configDirty}
                    >
                      Salva modifiche caselle
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Typography variant="h6">Notifiche</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={configDraft.notifications.enabled}
                      onChange={(event) => handleNotificationsToggle(event.target.checked)}
                    />
                  }
                  label="Abilita invio notifiche"
                />
                {configDraft.notifications.enabled ? (
                  <Stack spacing={2}>
                    <TextField
                      label="Invia a"
                      helperText="Indirizzi email separati da virgola o nuova linea"
                      value={recipientsPreview}
                      onChange={(event) => handleNotificationsRecipients(event.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <TextField
                      label="Invio"
                      helperText="always / error"
                      value={configDraft.notifications.send_on ?? "always"}
                      onChange={(event) => updateDraft((draft) => { draft.notifications.send_on = event.target.value; })}
                      fullWidth
                    />
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">SMTP</Typography>
                      <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Host"
                            value={configDraft.notifications.smtp?.host ?? ""}
                            onChange={(event) => handleNotificationSmtpChange("host", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 2 }}>
                          <TextField
                            label="Porta"
                            type="number"
                            value={configDraft.notifications.smtp?.port ?? ""}
                            onChange={(event) => handleNotificationSmtpChange("port", Number(event.target.value) || 0)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 3 }}>
                          <TextField
                            label="Utente"
                            value={configDraft.notifications.smtp?.username ?? ""}
                            onChange={(event) => handleNotificationSmtpChange("username", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 3 }}>
                          <TextField
                            label="Password"
                            type="password"
                            value={configDraft.notifications.smtp?.password ?? ""}
                            onChange={(event) => handleNotificationSmtpChange("password", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Mittente"
                            value={configDraft.notifications.smtp?.sender ?? ""}
                            onChange={(event) => handleNotificationSmtpChange("sender", event.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={configDraft.notifications.smtp?.use_tls ?? true}
                                onChange={(event) => handleNotificationSmtpChange("use_tls", event.target.checked)}
                              />
                            }
                            label="Usa TLS"
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>

              <Stack spacing={2}>
                <Typography variant="h6">Accesso API</Typography>
                <FormControl fullWidth>
                  <InputLabel id="auth-provider-label">Provider autenticazione</InputLabel>
                  <Select
                    labelId="auth-provider-label"
                    id="auth-provider"
                    label="Provider autenticazione"
                    value={authProvider}
                    onChange={(event) => handleAuthProviderChange(event.target.value as "local" | "keycloak")}
                  >
                    <MenuItem value="local">Locale</MenuItem>
                    <MenuItem value="keycloak">Keycloak (OIDC)</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={configDraft.auth.enabled}
                      onChange={(event) => handleAuthToggle(event.target.checked)}
                    />
                  }
                  label="Richiedi autenticazione"
                />
                {configDraft.auth.enabled ? (
                  isKeycloakProvider ? (
                    <Stack spacing={2}>
                      <Alert severity="info">
                        L'accesso alla console avverrà tramite Keycloak; le credenziali locali sono disattivate.
                      </Alert>
                      <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="Issuer URL"
                            value={configDraft.auth.issuer_url ?? ""}
                            onChange={(event) => updateDraft((draft) => { draft.auth.issuer_url = event.target.value; })}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="Client ID"
                            value={configDraft.auth.client_id ?? ""}
                            onChange={(event) => updateDraft((draft) => { draft.auth.client_id = event.target.value; })}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="Audience (opzionale)"
                            value={configDraft.auth.audience ?? ""}
                            onChange={(event) => updateDraft((draft) => { draft.auth.audience = event.target.value; })}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="JWKS URL (opzionale)"
                            helperText="Se vuoto verrà calcolato dall'issuer"
                            value={configDraft.auth.jwks_url ?? ""}
                            onChange={(event) => updateDraft((draft) => { draft.auth.jwks_url = event.target.value; })}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 4 }}>
                          <TextField
                            label="Cache JWKS (secondi)"
                            type="number"
                            value={configDraft.auth.jwks_cache_seconds ?? 3_600}
                            onChange={(event) => updateDraft((draft) => {
                              const value = Number(event.target.value);
                              draft.auth.jwks_cache_seconds = Number.isFinite(value) && value > 0 ? value : 3_600;
                            })}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                        <Grid size={{ xs: 1, md: 6 }}>
                          <TextField
                            label="Secret key JWT"
                            value={configDraft.auth.secret_key ?? ""}
                            onChange={(event) => updateDraft((draft) => { draft.auth.secret_key = event.target.value; })}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 1, md: 3 }}>
                          <TextField
                            label="Algoritmo"
                            select
                            value={configDraft.auth.algorithm ?? "HS256"}
                            onChange={(event) => updateDraft((draft) => { draft.auth.algorithm = event.target.value; })}
                            fullWidth
                          >
                            {(configDraft.auth.algorithms ?? ["HS256", "HS384", "HS512"]).map((algo) => (
                              <MenuItem key={algo} value={algo}>
                                {algo}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 1, md: 3 }}>
                          <TextField
                            label="Scadenza token (minuti)"
                            type="number"
                            value={configDraft.auth.token_expiry_minutes ?? 60}
                            onChange={(event) => updateDraft((draft) => {
                              draft.auth.token_expiry_minutes = Number(event.target.value) || 60;
                            })}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <Stack spacing={2}>
                        <Typography variant="subtitle1">Utenti autorizzati</Typography>
                        {(configDraft.auth.users ?? []).map((user, index) => (
                          <Stack key={`${user.username || "user"}-${index}`} direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                            <TextField
                              label="Username"
                              value={user.username}
                              onChange={(event) => handleAuthUserChange(index, "username", event.target.value)}
                              fullWidth
                            />
                            <TextField
                              label="Password"
                              type="password"
                              value={user.password}
                              onChange={(event) => handleAuthUserChange(index, "password", event.target.value)}
                              fullWidth
                            />
                            <IconButton color="error" onClick={() => handleRemoveAuthUser(index)}>
                              <DeleteRoundedIcon />
                            </IconButton>
                          </Stack>
                        ))}
                        <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleAddAuthUser}>
                          Aggiungi utente
                        </Button>
                      </Stack>
                    </Stack>
              )
            ) : null}
          </Stack>

          <Card>
            <CardHeader title="Token API" subheader="Genera token con scadenza per l'accesso applicativo" />
            <CardContent>
              <Stack spacing={2}>
                {generatedToken ? (
                  <Alert severity="success">
                    Token generato: <strong>{generatedToken}</strong>{" "}
                    <Button
                      size="small"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => handleCopy(generatedToken, "Token API")}
                      sx={{ ml: 1 }}
                    >
                      Copia
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Salvalo ora: per sicurezza non verrà più mostrato.
                    </Typography>
                  </Alert>
                ) : null}

                <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Label (opzionale)"
                      value={newTokenLabel}
                      onChange={(e) => setNewTokenLabel(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="token-expiry-label">Scadenza</InputLabel>
                      <Select
                        labelId="token-expiry-label"
                        label="Scadenza"
                        value={newTokenExpiry}
                        onChange={(e) => setNewTokenExpiry(Number(e.target.value))}
                      >
                        {[1, 7, 30, 90, 180, 365].map((days) => (
                          <MenuItem key={days} value={days}>
                            {days} {days === 1 ? "giorno" : "giorni"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="token-perms-label">Permessi</InputLabel>
                      <Select
                        labelId="token-perms-label"
                        label="Permessi"
                        value={tokenPermissions}
                        onChange={(e) => setTokenPermissions(e.target.value as "read" | "write")}
                      >
                        <MenuItem value="read">Sola lettura</MenuItem>
                        <MenuItem value="write">Lettura e scrittura</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="token-scope-label">Ambito account</InputLabel>
                      <Select
                        labelId="token-scope-label"
                        label="Ambito account"
                        value={tokenScopeAll ? "__all__" : tokenAccounts[0] ?? ""}
                        onChange={(e) => {
                          const val = e.target.value as string;
                          if (val === "__all__") {
                            setTokenScopeAll(true);
                            setTokenAccounts([]);
                          } else {
                            setTokenScopeAll(false);
                            setTokenAccounts(val ? [val] : []);
                          }
                        }}
                      >
                        <MenuItem value="__all__">Tutti gli account</MenuItem>
                        {(configDraft?.accounts ?? []).map((acc) => (
                          <MenuItem key={acc.username} value={acc.username}>
                            {acc.username}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {!tokenScopeAll && (
                      <TextField
                        label="Account aggiuntivi (uno per riga)"
                        value={tokenAccounts.join("\n")}
                        onChange={(e) => setTokenAccounts(e.target.value.split(/\s+/).filter(Boolean))}
                        multiline
                        minRows={3}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  startIcon={<KeyRoundedIcon />}
                  onClick={handleCreateToken}
                  disabled={createToken.isPending}
                >
                  {createToken.isPending ? "Generazione..." : "Genera token"}
                </Button>

                <Divider />

                {tokensQuery.isLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Caricamento token...
                    </Typography>
                  </Stack>
                ) : tokensQuery.isError ? (
                  <Alert severity="error">Impossibile caricare i token API.</Alert>
                ) : (tokensQuery.data?.tokens ?? []).length === 0 ? (
                  <Alert severity="info">Nessun token API creato.</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Label</TableCell>
                              <TableCell>Permessi</TableCell>
                              <TableCell>Account</TableCell>
                              <TableCell>Creato</TableCell>
                              <TableCell>Scadenza</TableCell>
                              <TableCell>Ultimo uso</TableCell>
                              <TableCell align="right">Azioni</TableCell>
                            </TableRow>
                      </TableHead>
                      <TableBody>
                        {tokensQuery.data?.tokens.map((token) => (
                          <TableRow key={token.id} hover>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography fontWeight={600}>{token.label || "(senza label)"}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {token.id}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{(token.permissions || ["read"]).join(", ")}</TableCell>
                            <TableCell>
                              {token.accounts && token.accounts.length > 0
                                ? (token.accounts.includes("*") ? "Tutti" : token.accounts.join(", "))
                                : "Tutti"}
                            </TableCell>
                            <TableCell>{formatDateTime(token.created_at)}</TableCell>
                            <TableCell>{token.expires_at ? formatDateTime(token.expires_at) : "Nessuna"}</TableCell>
                            <TableCell>{token.last_used_at ? formatDateTime(token.last_used_at) : "Mai"}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Elimina token">
                                <IconButton color="error" onClick={() => handleDeleteToken(token.id)}>
                                  <DeleteRoundedIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Stack spacing={2}>
            <Typography variant="h6">Restrizioni IP</Typography>
                <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Whitelist IP/CIDR"
                      value={allowInput}
                      onChange={(event) => handleAllowChange(event.target.value)}
                      helperText="Indirizzi o reti ammessi (uno per riga). Vuoto = tutti."
                      multiline
                      minRows={3}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Blacklist IP/CIDR"
                      value={denyInput}
                      onChange={(event) => handleDenyChange(event.target.value)}
                      helperText="Indirizzi o reti bloccati (uno per riga)."
                      multiline
                      minRows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Stack spacing={2}>
                <Typography variant="h6">Altre opzioni</Typography>
                <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                  <Grid size={{ xs: 1, md: 4 }}>
                    <TextField
                      label="Tentativi massimi"
                      type="number"
                      value={configDraft.retry_policy?.max_retries ?? 3}
                      onChange={(event) => updateDraft((draft) => { draft.retry_policy.max_retries = Number(event.target.value) || 3; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 4 }}>
                    <TextField
                      label="Attesa iniziale (s)"
                      type="number"
                      value={configDraft.retry_policy?.initial_delay ?? 5}
                      onChange={(event) => updateDraft((draft) => { draft.retry_policy.initial_delay = Number(event.target.value) || 5; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 4 }}>
                    <TextField
                      label="Fattore backoff"
                      type="number"
                      value={configDraft.retry_policy?.backoff_multiplier ?? 2}
                      onChange={(event) => updateDraft((draft) => { draft.retry_policy.backoff_multiplier = Number(event.target.value) || 2; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Timeout IMAP (s)"
                      type="number"
                      value={configDraft.imap?.timeout ?? 30}
                      onChange={(event) => updateDraft((draft) => { draft.imap.timeout = Number(event.target.value) || 30; })}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1, md: 6 }}>
                    <TextField
                      label="Batch IMAP"
                      type="number"
                      value={configDraft.imap?.batch_size ?? 100}
                      onChange={(event) => updateDraft((draft) => { draft.imap.batch_size = Number(event.target.value) || 100; })}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          ) : null}
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            onClick={() => {
              if (configQuery.data) {
                const normalized = cloneConfig(configQuery.data);
                normalized.access_control = normalized.access_control ?? { allow: [], deny: [] };
                setConfigDraft(normalized);
                setAllowInput((normalized.access_control?.allow ?? []).join("\n"));
                setDenyInput((normalized.access_control?.deny ?? []).join("\n"));
                setConfigDirty(false);
                setPendingFullSyncFrom(null);
                setPendingFullSyncAccount(null);
              }
            }}
            disabled={!configDirty || updateConfig.isPending || validateConfig.isPending}
          >
            Annulla modifiche
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={handleSaveConfig}
            disabled={!configDraft || updateConfig.isPending || validateConfig.isPending || !configDirty}
          >
            {updateConfig.isPending || validateConfig.isPending ? "Salvataggio..." : "Salva configurazione"}
          </Button>
        </CardActions>
      </Card>

      <Grid container spacing={3} columns={{ xs: 1, md: 12 }}>
        <Grid size={{ xs: 1, md: 6 }}>
          <Card>
            <CardHeader title="Aspetto" subheader="Seleziona il tema predefinito dell'interfaccia" />
            <CardContent>
              <RadioGroup row value={mode} onChange={handleThemeChange}>
                <FormControlLabel value="system" control={<Radio />} label="Automatico" />
                <FormControlLabel value="light" control={<Radio />} label="Chiaro" />
                <FormControlLabel value="dark" control={<Radio />} label="Scuro" />
              </RadioGroup>
              <Alert severity="info" sx={{ mt: 2 }}>
                Il selettore nell'header consente di cambiare tema rapidamente; qui puoi definire il comportamento di default.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, md: 6 }}>
          <Card>
            <CardHeader title="Aggiornamento dati" subheader="Intervalli di polling per dashboard e attività" />
            <CardContent>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Dashboard
                  </Typography>
                  <Select
                    value={statusRefreshMs}
                    onChange={(event) => setStatusRefreshMs(Number(event.target.value))}
                  >
                    {refreshOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Attività e notifiche
                  </Typography>
                  <Select
                    value={activityRefreshMs}
                    onChange={(event) => setActivityRefreshMs(Number(event.target.value))}
                  >
                    {refreshOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, md: 6 }}>
          <Card>
            <CardHeader title="Notifiche" subheader="Preferenze per gli avvisi in tempo reale" />
            <CardContent>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notificationsAutoDismiss}
                      onChange={(event) => setNotificationsAutoDismiss(event.target.checked)}
                    />
                  }
                  label="Chiudi automaticamente gli avvisi"
                />
                <FormControl fullWidth disabled={!notificationsAutoDismiss}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Durata predefinita
                  </Typography>
                  <Select
                    value={notificationsDurationMs}
                    onChange={(event) => setNotificationsDurationMs(Number(event.target.value))}
                  >
                    {notificationDurations.map((duration) => (
                      <MenuItem key={duration} value={duration}>
                        {formatMillis(duration)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 1, md: 6 }}>
          <Card>
            <CardHeader title="Ricerca" subheader="Preferenze predefinite per la pagina di ricerca" />
            <CardContent>
              <FormControl fullWidth>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Risultati per pagina (default)
                </Typography>
                <Select
                  value={searchDefaultRows}
                  onChange={(event) => setSearchDefaultRows(Number(event.target.value))}
                >
                  {searchRowsOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardHeader
          title="Stato sistema"
          subheader="Monitoraggio schedulatore e job correnti"
          action={
            <Tooltip title="Copia URL base API">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={() => handleCopy(API_BASE_URL, "URL API")}
              >
                Copia URL API
              </Button>
            </Tooltip>
          }
        />
        <CardContent>
          {isError ? (
            <Alert severity="error">Impossibile ottenere lo stato del sistema: {extractApiError(error)}</Alert>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Chip
                  label={schedulerRunning ? "Scheduler attivo" : "Scheduler fermo"}
                  color={schedulerRunning ? "success" : "default"}
                />
                <Chip
                  label={jobInProgress ? "Job in esecuzione" : "Nessun job in corso"}
                  color={jobInProgress ? "warning" : "default"}
                />
                <Button variant="outlined" onClick={resetDefaults}>
                  Ripristina preferenze locali
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Prossimo run giornaliero: {nextDailyRunLabel || "n/d"}
              </Typography>
              {sourceSyncEnabledStatus ? (
                <Typography variant="body2" color="text.secondary">
                  Prossimo source-sync ({sourceSyncIntervalHoursStatus === 1 ? "ogni ora" : `ogni ${sourceSyncIntervalHoursStatus} ore`}): {nextSourceSyncLabel || "n/d"}
                </Typography>
              ) : null}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="S3 Tiering"
          subheader="Monitoraggio operativo e azioni amministrative per offload/prune"
          action={
            <Button
              variant="outlined"
              size="small"
              onClick={() => queryClient.invalidateQueries({ queryKey: ["system", "s3-tiering", "status"] })}
              disabled={!canManageS3Tiering}
            >
              Aggiorna
            </Button>
          }
        />
        <CardContent>
          {!canManageSettings ? (
            <Alert severity="warning">Permessi insufficienti per visualizzare il tiering S3.</Alert>
          ) : !canManageS3Tiering ? (
            <Alert severity="warning">Operazioni e configurazione S3 tiering riservate agli amministratori.</Alert>
          ) : s3TieringStatusQuery.isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Caricamento stato S3 tiering...</Typography>
            </Stack>
          ) : s3TieringStatusQuery.isError ? (
            <Alert severity="error">Errore stato S3 tiering: {extractApiError(s3TieringStatusQuery.error)}</Alert>
          ) : s3TieringStatus ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Chip label={`Mode: ${s3TieringStatus.backup_mode || "n/d"}`} color={s3TieringStatus.enabled ? "success" : "default"} />
                <Chip label={`Bucket: ${s3TieringStatus.bucket || "n/d"}`} />
                <Chip label={`Prune: ${s3TieringStatus.allow_local_prune ? "abilitato" : "disabilitato"}`} color={s3TieringStatus.allow_local_prune ? "warning" : "default"} />
                <Chip label={`Credenziali AWS: ${s3TieringStatus.aws_credentials_present ? "ok" : "mancanti"}`} color={s3TieringStatus.aws_credentials_present ? "success" : "error"} />
              </Stack>

              <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                <Grid size={{ xs: 1, md: 4 }}>
                  <TextField label="Region" value={s3TieringStatus.region ?? ""} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 1, md: 4 }}>
                  <TextField label="Prefix" value={s3TieringStatus.prefix ?? ""} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
                <Grid size={{ xs: 1, md: 4 }}>
                  <TextField label="Spazio libero (GiB)" value={String(s3TieringStatus.free_space_gb ?? "n/d")} fullWidth InputProps={{ readOnly: true }} />
                </Grid>
              </Grid>

              {s3TieringStatus.lock.present ? (
                <Alert severity={s3TieringStatus.lock.stale ? "warning" : "info"}>
                  Lock attivo: {s3TieringStatus.lock.path}
                  {s3TieringStatus.lock.pid ? ` (pid=${s3TieringStatus.lock.pid})` : ""}
                  {typeof s3TieringStatus.lock.age_seconds === "number" ? `, eta ${s3TieringStatus.lock.age_seconds}s` : ""}
                </Alert>
              ) : (
                <Alert severity="success">Nessun lock attivo.</Alert>
              )}

              {s3LastRun ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Ultimo ciclo</Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                    <Chip label={`Stato: ${s3LastRun.status ?? "n/d"}`} color={s3LastRun.status === "failed" ? "error" : (s3LastRun.status === "success" ? "success" : "default")} />
                    <Chip label={`Fase: ${s3LastRun.phase ?? "n/d"}`} />
                    <Chip label={`Durata: ${s3LastRun.duration_seconds ?? 0}s`} />
                    <Chip label={`Offload: ${s3LastRun.offloaded_accounts}`} />
                    <Chip label={`Pruned: ${s3LastRun.pruned_files}`} />
                    <Chip label={`Skipped: ${s3LastRun.skipped_files}`} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Inizio: {formatDateTime(s3LastRun.started_at ?? null) || "n/d"} | Fine: {formatDateTime(s3LastRun.ended_at ?? null) || "n/d"} | Messaggio: {s3LastRun.message ?? "n/d"}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Nessuna esecuzione registrata.</Typography>
              )}

              <Divider />

              <Typography variant="subtitle2">Configurazione tiering (parita manage.sh)</Typography>
              <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
                <Grid size={{ xs: 1, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel id="s3-tiering-mode-label">Backup mode</InputLabel>
                    <Select
                      labelId="s3-tiering-mode-label"
                      label="Backup mode"
                      value={s3TieringForm.backup_mode}
                      onChange={(event) => {
                        const value = event.target.value as "local_tiered_s3" | "local_mirror_s3";
                        setS3TieringForm((prev) => ({ ...prev, backup_mode: value }));
                        setS3TieringDirty(true);
                      }}
                    >
                      <MenuItem value="local_tiered_s3">local_tiered_s3</MenuItem>
                      <MenuItem value="local_mirror_s3">local_mirror_s3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 1, md: 3 }}>
                  <TextField
                    label="Bucket"
                    value={s3TieringForm.bucket}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, bucket: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 3 }}>
                  <TextField
                    label="Region"
                    value={s3TieringForm.region}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, region: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 3 }}>
                  <TextField
                    label="Prefix"
                    value={s3TieringForm.prefix}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, prefix: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 4 }}>
                  <TextField
                    label="Endpoint URL"
                    value={s3TieringForm.endpoint_url}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, endpoint_url: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 2 }}>
                  <TextField
                    label="Retention HOT (gg)"
                    type="number"
                    value={s3TieringForm.local_hot_retention_days}
                    onChange={(event) => {
                      const value = Number(event.target.value || 0);
                      setS3TieringForm((prev) => ({ ...prev, local_hot_retention_days: value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 2 }}>
                  <TextField
                    label="Min free (GiB)"
                    type="number"
                    value={s3TieringForm.min_free_space_gb}
                    onChange={(event) => {
                      const value = Number(event.target.value || 0);
                      setS3TieringForm((prev) => ({ ...prev, min_free_space_gb: value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={s3TieringForm.allow_local_prune}
                        onChange={(event) => {
                          setS3TieringForm((prev) => ({ ...prev, allow_local_prune: event.target.checked }));
                          setS3TieringDirty(true);
                        }}
                      />
                    }
                    label="Abilita prune locale"
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 6 }}>
                  <TextField
                    label="AWS_ACCESS_KEY_ID (opzionale)"
                    value={s3TieringForm.aws_access_key_id}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, aws_access_key_id: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 1, md: 6 }}>
                  <TextField
                    label="AWS_SECRET_ACCESS_KEY (opzionale)"
                    type="password"
                    value={s3TieringForm.aws_secret_access_key}
                    onChange={(event) => {
                      setS3TieringForm((prev) => ({ ...prev, aws_secret_access_key: event.target.value }));
                      setS3TieringDirty(true);
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSaveS3TieringConfig}
                  disabled={configureS3Tiering.isPending || runS3TieringAction.isPending}
                >
                  {configureS3Tiering.isPending ? "Salvataggio..." : "Salva configurazione"}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Equivalente UI a `manage.sh s3-tiering-configure`.
                </Typography>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Button variant="outlined" onClick={() => runS3Tiering("validate")} disabled={runS3TieringAction.isPending || configureS3Tiering.isPending}>Valida</Button>
                <Button variant="outlined" onClick={() => runS3Tiering("dry-run")} disabled={runS3TieringAction.isPending || configureS3Tiering.isPending}>Dry-run</Button>
                <Button variant="outlined" onClick={() => runS3Tiering("offload")} disabled={runS3TieringAction.isPending || configureS3Tiering.isPending}>Offload</Button>
                <Button variant="outlined" color="warning" onClick={() => runS3Tiering("prune")} disabled={runS3TieringAction.isPending || configureS3Tiering.isPending}>Prune</Button>
                <Button variant="contained" onClick={() => runS3Tiering("cycle")} disabled={runS3TieringAction.isPending || configureS3Tiering.isPending}>Cycle</Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => runS3Tiering("unlock-stale")}
                  disabled={runS3TieringAction.isPending || configureS3Tiering.isPending || !s3TieringStatus.lock.present}
                >
                  Unlock stale
                </Button>
              </Stack>

              {runS3TieringAction.isPending ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Azione S3 tiering in esecuzione...</Typography>
                </Stack>
              ) : null}

              {s3TieringOutput.length > 0 ? (
                <TextField
                  label="Output ultima azione"
                  value={s3TieringOutput.join("\n")}
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              ) : null}
            </Stack>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Manutenzione indice metadati"
          subheader="Backfill/rebuild dell'indice SQLite da index.json storico"
        />
        <CardContent>
          <Stack spacing={2}>
            <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
              <Grid size={{ xs: 1, md: 4 }}>
                <TextField
                  label="Account (opzionale)"
                  value={rebuildAccount}
                  onChange={(event) => setRebuildAccount(event.target.value)}
                  placeholder="es. casella@pec.it"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 1, md: 4 }}>
                <TextField
                  label="Da data"
                  type="date"
                  value={rebuildStartDate}
                  onChange={(event) => setRebuildStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 1, md: 4 }}>
                <TextField
                  label="A data"
                  type="date"
                  value={rebuildEndDate}
                  onChange={(event) => setRebuildEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleRebuildMetadataIndex}
                disabled={rebuildMetadataIndex.isPending || Boolean(metadataStatus?.running)}
              >
                {rebuildMetadataIndex.isPending || metadataStatus?.running ? "Rebuild in corso..." : "Avvia rebuild indice"}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                Il job parte in background lato server; puoi continuare a usare l'app.
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Chip
                label={metadataStatus?.running ? "Indice: rebuild in corso" : "Indice: idle"}
                color={metadataStatus?.running ? "warning" : "default"}
              />
              {metadataStatus?.last_started_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Avvio: {formatDateTime(metadataStatus.last_started_iso)}
                </Typography>
              ) : null}
              {metadataStatus?.last_completed_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Fine: {formatDateTime(metadataStatus.last_completed_iso)}
                </Typography>
              ) : null}
            </Stack>

            {metadataStatus?.last_error ? (
              <Alert severity="error">Ultimo errore rebuild: {metadataStatus.last_error}</Alert>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Backup database metadati"
          subheader="Esporta un full backup del DB metadati (SQLite/PostgreSQL) in formato JSONL compresso"
        />
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                onClick={handleExportDatabaseBackup}
                disabled={exportDatabaseBackup.isPending || Boolean(dbBackupStatus?.running)}
              >
                {exportDatabaseBackup.isPending || dbBackupStatus?.running ? "Export in corso..." : "Esporta full backup DB"}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                L'export viene eseguito in background lato server e genera file scaricabili da questa sezione.
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Chip
                label={dbBackupStatus?.running ? "Backup DB: in corso" : "Backup DB: idle"}
                color={dbBackupStatus?.running ? "warning" : "default"}
              />
              {dbBackupStatus?.last_started_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Avvio: {formatDateTime(dbBackupStatus.last_started_iso)}
                </Typography>
              ) : null}
              {dbBackupStatus?.last_completed_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Fine: {formatDateTime(dbBackupStatus.last_completed_iso)}
                </Typography>
              ) : null}
            </Stack>

            {dbBackupStatus?.last_error ? (
              <Alert severity="error">Ultimo errore export DB: {dbBackupStatus.last_error}</Alert>
            ) : null}

            <Divider />

            <Grid container spacing={2} columns={{ xs: 1, md: 12 }}>
              <Grid size={{ xs: 1, md: 5 }}>
                <FormControl fullWidth>
                  <InputLabel id="db-backup-select-label">Backup da importare</InputLabel>
                  <Select
                    labelId="db-backup-select-label"
                    value={selectedDbBackup}
                    label="Backup da importare"
                    onChange={(event) => setSelectedDbBackup(String(event.target.value))}
                  >
                    {dbBackupEntries.map((entry) => (
                      <MenuItem key={entry.filename} value={entry.filename}>
                        {entry.filename}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 1, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="db-import-mode-label">Modalita import</InputLabel>
                  <Select
                    labelId="db-import-mode-label"
                    value={dbImportMode}
                    label="Modalita import"
                    onChange={(event) => setDbImportMode(event.target.value as "replace" | "merge")}
                  >
                    <MenuItem value="replace">replace</MenuItem>
                    <MenuItem value="merge">merge</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 1, md: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dbImportDryRun}
                      onChange={(event) => setDbImportDryRun(event.target.checked)}
                    />
                  }
                  label="Dry-run (no write)"
                />
              </Grid>
            </Grid>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                onClick={handleValidateDatabaseBackup}
                disabled={!selectedDbBackup || validateDatabaseBackupImport.isPending || Boolean(dbBackupImportStatus?.running)}
              >
                {validateDatabaseBackupImport.isPending ? "Validazione..." : "Valida backup"}
              </Button>
              <Button
                variant="contained"
                onClick={handleImportDatabaseBackup}
                disabled={!selectedDbBackup || importDatabaseBackup.isPending || Boolean(dbBackupImportStatus?.running)}
              >
                {importDatabaseBackup.isPending || dbBackupImportStatus?.running
                  ? "Import in corso..."
                  : dbImportDryRun
                    ? "Avvia dry-run import"
                    : "Avvia import backup"}
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Chip
                label={dbBackupImportStatus?.running ? "Import DB: in corso" : "Import DB: idle"}
                color={dbBackupImportStatus?.running ? "warning" : "default"}
              />
              {dbBackupImportStatus?.last_started_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Avvio import: {formatDateTime(dbBackupImportStatus.last_started_iso)}
                </Typography>
              ) : null}
              {dbBackupImportStatus?.last_completed_iso ? (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Fine import: {formatDateTime(dbBackupImportStatus.last_completed_iso)}
                </Typography>
              ) : null}
            </Stack>

            {dbBackupImportStatus?.last_error ? (
              <Alert severity="error">Ultimo errore import DB: {dbBackupImportStatus.last_error}</Alert>
            ) : null}

            {dbImportValidation ? (
              <Alert severity="success">
                Validazione OK: {String(dbImportValidation.filename)} - righe {Number(dbImportValidation.row_count || 0).toLocaleString()} - checksum {String(dbImportValidation.checksum_sha256 || "").slice(0, 16)}...
              </Alert>
            ) : null}

            {dbBackupImportStatus?.last_result ? (
              <Alert severity="info">
                Ultimo import: mode={String(dbBackupImportStatus.last_result.mode)} dry_run={String(dbBackupImportStatus.last_result.dry_run)} validated_rows={String(dbBackupImportStatus.last_result.validated_rows)} imported_rows={String(dbBackupImportStatus.last_result.imported_rows)}
              </Alert>
            ) : null}

            {databaseBackupsQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Caricamento backup database...</Typography>
              </Stack>
            ) : databaseBackupsQuery.isError ? (
              <Alert severity="error">Impossibile caricare la lista backup DB.</Alert>
            ) : dbBackupEntries.length === 0 ? (
              <Alert severity="info">Nessun backup database disponibile.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Creato</TableCell>
                      <TableCell>Backend</TableCell>
                      <TableCell align="right">Righe</TableCell>
                      <TableCell align="right">Dimensione</TableCell>
                      <TableCell>Checksum</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dbBackupEntries.map((entry) => (
                      <TableRow key={entry.filename}>
                        <TableCell>{formatDateTime(entry.created_at_iso)}</TableCell>
                        <TableCell>{entry.backend}</TableCell>
                        <TableCell align="right">{entry.row_count.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatBytes(entry.size_bytes)}</TableCell>
                        <TableCell>
                          <Tooltip title={entry.checksum_sha256}>
                            <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                              {entry.checksum_sha256.slice(0, 16)}...
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => handleDownloadDatabaseBackup(entry.filename)}>
                              Download
                            </Button>
                            <Button size="small" variant="text" onClick={() => handleDownloadDatabaseBackup(entry.checksum_filename)}>
                              SHA256
                            </Button>
                            <Button size="small" variant="text" onClick={() => handleDownloadDatabaseBackup(entry.manifest_filename)}>
                              Manifest
                            </Button>
                            <Tooltip title="Copia link diretto download (richiede sessione autenticata)">
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(buildDatabaseBackupDownloadUrl(entry.filename), "URL backup DB")}
                              >
                                <ContentCopyRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={s3PrunePrecheckOpen} onClose={() => setS3PrunePrecheckOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Safety gate prune locale S3 tiering</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {s3PrunePrecheck ? (
              <>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                  <Chip label={`Candidati: ${s3PrunePrecheck.candidate_files}`} />
                  <Chip label={`Spazio stimato: ${formatBytes(s3PrunePrecheck.candidate_size_bytes)}`} />
                  <Chip label={`Spazio libero: ${s3PrunePrecheck.free_space_gb ?? "n/d"} GiB`} />
                  <Chip label={`Soglia minima: ${s3PrunePrecheck.min_free_space_gb} GiB`} />
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Ultimo offload: {s3PrunePrecheck.last_offload_status ?? "n/d"} | Fine: {formatDateTime(s3PrunePrecheck.last_offload_ended_at ?? null) || "n/d"}
                </Typography>

                <Typography variant="subtitle2">Account coinvolti</Typography>
                <Typography variant="body2" color="text.secondary">
                  {s3PrunePrecheck.impacted_accounts.length > 0
                    ? s3PrunePrecheck.impacted_accounts.join(", ")
                    : "Nessun account candidato"}
                </Typography>

                {!s3PrunePrecheck.allowed ? (
                  <Alert severity="error">
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">Prerequisiti non soddisfatti</Typography>
                      {s3PrunePrecheck.reasons.map((reason) => (
                        <Typography key={reason} variant="body2">- {reason}</Typography>
                      ))}
                    </Stack>
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    Prune definitivo: verranno eliminati file locali oltre retention HOT dopo verifica oggetti su S3.
                  </Alert>
                )}
              </>
            ) : (
              <Alert severity="info">Precheck non disponibile.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setS3PrunePrecheckOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmS3PruneFromPrecheck}
            disabled={runS3TieringAction.isPending || !s3PrunePrecheck?.allowed}
          >
            {runS3TieringAction.isPending ? "Esecuzione..." : "Conferma prune"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createUserOpen} onClose={() => setCreateUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo utente</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={createUserUsername}
              onChange={(event) => setCreateUserUsername(event.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Email (opzionale)"
              value={createUserEmail}
              onChange={(event) => setCreateUserEmail(event.target.value)}
              fullWidth
            />
            <TextField
              label="Password iniziale"
              type="password"
              value={createUserPassword}
              onChange={(event) => setCreateUserPassword(event.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="create-user-role-label">Ruolo</InputLabel>
              <Select
                labelId="create-user-role-label"
                label="Ruolo"
                value={createUserRole}
                onChange={(event) => setCreateUserRole(String(event.target.value))}
              >
                <MenuItem value="pec-admin">pec-admin</MenuItem>
                <MenuItem value="pec-operator">pec-operator</MenuItem>
                <MenuItem value="pec-legal">pec-legal</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateUserOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreateIamUser} disabled={createIamUser.isPending}>
            {createIamUser.isPending ? "Creazione..." : "Crea utente"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetPasswordTarget)} onClose={() => setResetPasswordTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reimposta password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Utente: {resetPasswordTarget?.username}
            </Typography>
            <TextField
              label="Nuova password"
              type="password"
              value={resetPasswordValue}
              onChange={(event) => setResetPasswordValue(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordTarget(null)}>Annulla</Button>
          <Button variant="contained" onClick={handleResetIamPassword} disabled={resetIamUserPassword.isPending}>
            {resetIamUserPassword.isPending ? "Salvataggio..." : "Salva password"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo gruppo</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label="Nome gruppo"
            value={createGroupName}
            onChange={(event) => setCreateGroupName(event.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreateIamGroup} disabled={createIamGroup.isPending}>
            {createIamGroup.isPending ? "Creazione..." : "Crea gruppo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renameGroupTarget)} onClose={() => setRenameGroupTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Rinomina gruppo</DialogTitle>
        <DialogContent>
          <TextField
            sx={{ mt: 1 }}
            label="Nuovo nome"
            value={renameGroupName}
            onChange={(event) => setRenameGroupName(event.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameGroupTarget(null)}>Annulla</Button>
          <Button variant="contained" onClick={handleRenameIamGroup} disabled={updateIamGroup.isPending}>
            {updateIamGroup.isPending ? "Salvataggio..." : "Rinomina"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(groupMembershipTarget && groupMembershipMode)}
        onClose={() => {
          setGroupMembershipTarget(null);
          setGroupMembershipMode(null);
          setGroupMembershipGroupId("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{groupMembershipMode === "add" ? "Assegna gruppo" : "Rimuovi gruppo"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Utente: {groupMembershipTarget?.username}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="membership-group-label">Gruppo</InputLabel>
              <Select
                labelId="membership-group-label"
                label="Gruppo"
                value={groupMembershipGroupId}
                onChange={(event) => setGroupMembershipGroupId(String(event.target.value))}
              >
                {(iamGroupsQuery.data?.groups ?? [])
                  .filter((group) => {
                    if (!groupMembershipTarget || !groupMembershipMode) {
                      return true;
                    }
                    const belongs = groupMembershipTarget.groups.includes(group.name);
                    return groupMembershipMode === "add" ? !belongs : belongs;
                  })
                  .map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setGroupMembershipTarget(null);
              setGroupMembershipMode(null);
              setGroupMembershipGroupId("");
            }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={groupMembershipMode === "add" ? handleAssignUserGroup : handleRemoveUserGroup}
            disabled={addUserToGroup.isPending || removeUserFromGroup.isPending}
          >
            {groupMembershipMode === "add" ? "Assegna" : "Rimuovi"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteUserTarget)} onClose={() => setDeleteUserTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Elimina utente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Confermi eliminazione dell'utente {deleteUserTarget?.username}? L'operazione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserTarget(null)}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteUserTarget && handleDeleteIamUser(deleteUserTarget)}
            disabled={deleteIamUser.isPending}
          >
            {deleteIamUser.isPending ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteGroupTarget)} onClose={() => setDeleteGroupTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Elimina gruppo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Confermi eliminazione del gruppo {deleteGroupTarget?.name}? L'operazione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGroupTarget(null)}>Annulla</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteGroupTarget && handleDeleteIamGroup(deleteGroupTarget.id, deleteGroupTarget.name)}
            disabled={deleteIamGroup.isPending}
          >
            {deleteIamGroup.isPending ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>

      <BackupWizardDialog
        open={wizardOpen}
        initialAccount={wizardInitialAccount}
        onClose={handleWizardClose}
        onComplete={handleWizardComplete}
      />
    </Stack>
  );
}
